import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listTranscriptTypes, type TranscriptType } from "@/features/reference/api";
import { addRequests } from "@/features/eservices/api";
import type { ForWho } from "@/features/eservices/types";

interface AddRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  isBusiness: boolean;
  hasSpouse: boolean;
  onAdded: () => void;
}

const CURRENT_YEAR = new Date().getFullYear();

export function AddRequestDialog({
  open,
  onOpenChange,
  clientId,
  isBusiness,
  hasSpouse,
  onAdded,
}: AddRequestDialogProps) {
  const [types, setTypes] = useState<TranscriptType[]>([]);
  const [forWho, setForWho] = useState<ForWho>(isBusiness ? "B" : "T");
  const [transType, setTransType] = useState("");
  const [form, setForm] = useState("1040");
  const [yearFrom, setYearFrom] = useState(String(CURRENT_YEAR - 1));
  const [yearTo, setYearTo] = useState(String(CURRENT_YEAR - 1));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForWho(isBusiness ? "B" : "T");
    setError("");
    listTranscriptTypes()
      .then((all) => {
        const pullable = all.filter(
          (t) =>
            t.download &&
            (isBusiness ? t.appliesToBusinesses : t.appliesToIndividuals),
        );
        setTypes(pullable);
        if (pullable[0]) setTransType(pullable[0].transType);
      })
      .catch(() => setTypes([]));
  }, [open, isBusiness]);

  const whoOptions = useMemo(() => {
    if (isBusiness) return [{ value: "B", label: "Business" }] as const;
    const base = [{ value: "T", label: "Taxpayer" }];
    if (hasSpouse) base.push({ value: "S", label: "Spouse" });
    return base;
  }, [isBusiness, hasSpouse]);

  const submit = async () => {
    setError("");
    const from = Number(yearFrom);
    const to = Number(yearTo);
    if (!transType) return setError("Select a transcript type.");
    if (!from || !to || to < from) return setError("Enter a valid year range.");
    setSubmitting(true);
    try {
      const res = await addRequests(clientId, {
        forWho,
        transType,
        form,
        taxYearFrom: from,
        taxYearTo: to,
      });
      toast.success(`Added ${res.created} request(s); skipped ${res.skipped} duplicate(s).`);
      onAdded();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add requests.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add transcript request</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>For</Label>
            <Select value={forWho} onValueChange={(v) => setForWho(v as ForWho)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {whoOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Transcript type</Label>
            <Select value={transType} onValueChange={setTransType}>
              <SelectTrigger>
                <SelectValue placeholder="Select a type" />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.transType} value={t.transType}>
                    {t.shortDesc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="form">Form (drives period rules)</Label>
            <Input
              id="form"
              value={form}
              onChange={(e) => setForm(e.target.value.toUpperCase())}
              placeholder="1040, 941, 720, 730, CIVPEN"
            />
            <p className="text-muted-foreground text-xs">
              941/720/CIVPEN expand to quarters; 730 to months.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="yf">Year from</Label>
              <Input
                id="yf"
                inputMode="numeric"
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="yt">Year to</Label>
              <Input
                id="yt"
                inputMode="numeric"
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value)}
              />
            </div>
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
